import { useProjects } from '@/features/projects/projects.hooks';
import { useWorks } from '@/features/works/works.hooks';
import { useCallback } from 'react';

/** What a FORM may hold: unpicked levels read as null. */
interface CrmChainInput {
  project_id?: number | null;
  werk_id?: number | null;
  client_id?: number | null;
}

/** What a PAYLOAD may state: only werk_id is nullable (project-level). */
export type WithResolvedCrmChain<T> = Omit<
  T,
  'project_id' | 'werk_id' | 'client_id'
> & {
  project_id?: number;
  werk_id?: number | null;
  client_id?: number;
};

/** Thrown when the CRM data needed to complete a chain is not loaded. */
export class CrmChainUnresolvedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CrmChainUnresolvedError';
  }
}

/**
 * Completes the CRM chain on a payload that references a project or werk,
 * resolved from the most specific node; payloads that don't touch it pass
 * through. See docs/architecture/CRM_REFERENCE_PATTERN.md.
 *
 * Resolves against the CRM collections this hook has loaded, so it can fail —
 * and then it THROWS. A null project_id/client_id is not "unknown", it claims
 * the werk has no project and no customer, and the DB trigger would silently
 * overwrite the difference. Callers submit inside a try/catch.
 */
export function useCrmChainResolver() {
  const { data: projects } = useProjects();
  const { data: works } = useWorks();

  return useCallback(
    <T extends CrmChainInput>(payload: T): WithResolvedCrmChain<T> => {
      if (payload.werk_id) {
        const work = works?.find((w) => w.recnum === payload.werk_id);
        if (!work) {
          throw new CrmChainUnresolvedError(
            `Work ${payload.werk_id} is not loaded — cannot resolve its project and customer`,
          );
        }
        if (work.project_id == null || work.klant_id == null) {
          throw new CrmChainUnresolvedError(
            `Work ${payload.werk_id} has no project or customer to reference`,
          );
        }
        return {
          ...payload,
          project_id: Number(work.project_id),
          client_id: work.klant_id,
        } as WithResolvedCrmChain<T>;
      }
      if (payload.project_id) {
        const project = projects?.find((p) => p.recnum === payload.project_id);
        if (!project || project.klant_id == null) {
          throw new CrmChainUnresolvedError(
            `Project ${payload.project_id} is not loaded — cannot resolve its customer`,
          );
        }
        return {
          ...payload,
          werk_id: null,
          client_id: project.klant_id,
        } as WithResolvedCrmChain<T>;
      }
      if (
        'project_id' in payload ||
        'werk_id' in payload ||
        'client_id' in payload
      ) {
        // Chain touched but no node referenced. A ticket attaches at project
        // or werk level, so there is no chain to emit here.
        throw new CrmChainUnresolvedError(
          'A ticket must reference a project or work',
        );
      }
      // Chain untouched — nothing to narrow.
      return payload as WithResolvedCrmChain<T>;
    },
    [projects, works],
  );
}
